import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Globe, Plus, Edit2, Trash2, Copy, RefreshCw, Code, Settings,
  Check, X, ExternalLink, Eye, MessageSquare, Users, CheckCircle,
  AlertCircle, Clock, Palette, ChevronRight, ArrowLeft, Building2, ArrowRightLeft
} from 'lucide-react';
import { useRBAC } from '@/contexts/RBACContext';

const API = process.env.REACT_APP_BACKEND_URL;

const LiveChatWebsites = () => {
  const { token } = useAuth();
  const { isSuperAdmin } = useRBAC();
  const navigate = useNavigate();
  const location = useLocation();
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showScriptDialog, setShowScriptDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [editingWebsite, setEditingWebsite] = useState(null);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [scriptData, setScriptData] = useState(null);
  const [transferData, setTransferData] = useState({ target_organization_id: '', transfer_chats: true });
  
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    allowed_domains: [],
    status: 'active',
    brand_name: '',
    default_team_id: '',
    default_lead_source: 'Live Chat',
    widget_theme_color: '#10b981',
    widget_position: 'bottom-right',
    welcome_message: 'Hi! How can we help you today?',
    offline_message: "We're currently offline. Please leave a message and we'll get back to you.",
    enable_prechat_form: true,
    enable_lead_auto_creation: true,
    enable_visitor_tracking: true
  });

  useEffect(() => {
    fetchWebsites();
    fetchTeams();
    if (isSuperAdmin) {
      fetchOrganizations();
    }
  }, [token, isSuperAdmin]);

  // Auto-open add dialog when navigating to /new
  useEffect(() => {
    if (location.pathname.endsWith('/new')) {
      setShowAddDialog(true);
      setEditingWebsite(null);
      setFormData({
        name: '',
        domain: '',
        allowed_domains: [],
        status: 'active',
        brand_name: '',
        default_team_id: '',
        default_lead_source: 'Live Chat',
        widget_theme_color: '#10b981',
        widget_position: 'bottom-right',
        welcome_message: 'Hi! How can we help you today?',
        offline_message: "We're currently offline. Please leave a message and we'll get back to you.",
        enable_prechat_form: true,
        enable_lead_auto_creation: true,
        enable_visitor_tracking: true
      });
    }
  }, [location.pathname]);

  const fetchWebsites = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/live-chat/websites?token=${token}`);
      if (response.data.success) {
        setWebsites(response.data.websites);
      }
    } catch (error) {
      toast.error('Failed to load websites');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/api/teams?token=${token}`);
      setTeams(response.data?.teams || response.data || []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${API}/api/organizations?token=${token}`);
      setOrganizations(response.data?.organizations || response.data || []);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const handleTransfer = async () => {
    if (!transferData.target_organization_id) {
      toast.error('Please select a target organization');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/api/live-chat/websites/${selectedWebsite.id}/transfer?token=${token}`,
        transferData
      );
      if (response.data.success) {
        toast.success('Website transferred successfully');
        setShowTransferDialog(false);
        setSelectedWebsite(null);
        setTransferData({ target_organization_id: '', transfer_chats: true });
        fetchWebsites();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer website');
    }
  };

  const openTransferDialog = (website) => {
    setSelectedWebsite(website);
    setTransferData({ target_organization_id: '', transfer_chats: true });
    setShowTransferDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.domain) {
      toast.error('Name and domain are required');
      return;
    }

    try {
      if (editingWebsite) {
        const response = await axios.put(
          `${API}/api/live-chat/websites/${editingWebsite.id}?token=${token}`,
          formData
        );
        if (response.data.success) {
          toast.success('Website updated successfully');
          fetchWebsites();
        }
      } else {
        const response = await axios.post(
          `${API}/api/live-chat/websites?token=${token}`,
          formData
        );
        if (response.data.success) {
          toast.success('Website added successfully');
          fetchWebsites();
        }
      }
      closeDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save website');
    }
  };

  const handleDelete = async (websiteId) => {
    if (!confirm('Are you sure you want to delete this website?')) return;
    
    try {
      const response = await axios.delete(`${API}/api/live-chat/websites/${websiteId}?token=${token}`);
      if (response.data.success) {
        toast.success('Website deleted');
        fetchWebsites();
      }
    } catch (error) {
      toast.error('Failed to delete website');
    }
  };

  const openEditDialog = (website) => {
    setEditingWebsite(website);
    setFormData({
      name: website.name,
      domain: website.domain,
      allowed_domains: website.allowed_domains || [],
      status: website.status,
      brand_name: website.brand_name || '',
      default_team_id: website.default_team_id || '',
      default_lead_source: website.default_lead_source || 'Live Chat',
      widget_theme_color: website.widget_theme_color || '#10b981',
      widget_position: website.widget_position || 'bottom-right',
      welcome_message: website.welcome_message || '',
      offline_message: website.offline_message || '',
      enable_prechat_form: website.enable_prechat_form ?? true,
      enable_lead_auto_creation: website.enable_lead_auto_creation ?? true,
      enable_visitor_tracking: website.enable_visitor_tracking ?? true
    });
    setShowAddDialog(true);
  };

  const closeDialog = () => {
    setShowAddDialog(false);
    setEditingWebsite(null);
    setFormData({
      name: '',
      domain: '',
      allowed_domains: [],
      status: 'active',
      brand_name: '',
      default_team_id: '',
      default_lead_source: 'Live Chat',
      widget_theme_color: '#10b981',
      widget_position: 'bottom-right',
      welcome_message: 'Hi! How can we help you today?',
      offline_message: "We're currently offline. Please leave a message and we'll get back to you.",
      enable_prechat_form: true,
      enable_lead_auto_creation: true,
      enable_visitor_tracking: true
    });
    // Navigate back to main list if we were on /new
    if (location.pathname.endsWith('/new')) {
      navigate('/marketing/live-chat/websites');
    }
  };

  const openScriptDialog = async (website) => {
    setSelectedWebsite(website);
    try {
      const response = await axios.get(`${API}/api/live-chat/websites/${website.id}/tracking-script?token=${token}`);
      if (response.data.success) {
        setScriptData(response.data);
        setShowScriptDialog(true);
      }
    } catch (error) {
      toast.error('Failed to get tracking script');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const regenerateToken = async (websiteId) => {
    if (!confirm('Regenerating the token will invalidate the existing script. Continue?')) return;
    
    try {
      const response = await axios.post(`${API}/api/live-chat/websites/${websiteId}/regenerate-token?token=${token}`);
      if (response.data.success) {
        toast.success('Token regenerated');
        if (selectedWebsite?.id === websiteId) {
          openScriptDialog({ ...selectedWebsite, token: response.data.token });
        }
        fetchWebsites();
      }
    } catch (error) {
      toast.error('Failed to regenerate token');
    }
  };

  const getStatusBadge = (website) => {
    const verified = website.verification_status === 'verified';
    const active = website.status === 'active';
    
    if (!active) {
      return <Badge variant="secondary">Inactive</Badge>;
    }
    if (verified) {
      return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Verified</Badge>;
    }
    return <Badge variant="outline" className="text-amber-600 border-amber-200">Pending</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="live-chat-websites">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/marketing/live-chat')}>
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Websites & Channels</h1>
            <p className="text-sm text-slate-500">Manage your website tracking and chat widgets</p>
          </div>
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="gap-2" data-testid="add-website-btn">
          <Plus size={16} /> Add Website
        </Button>
      </div>

      {/* Websites List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : websites.length === 0 ? (
        <Card className="p-12 text-center">
          <Globe size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No websites configured</h3>
          <p className="text-slate-500 mb-4">Add your first website to start tracking visitors and managing live chats.</p>
          <Button onClick={() => setShowAddDialog(true)} className="gap-2">
            <Plus size={16} /> Add Website
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4">
          {websites.map(website => (
            <Card key={website.id} className="p-5 bg-white border-slate-200">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: website.widget_theme_color + '20' }}
                  >
                    <Globe size={24} style={{ color: website.widget_theme_color }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-slate-900">{website.name}</h3>
                      {getStatusBadge(website)}
                    </div>
                    <p className="text-sm text-slate-500">{website.domain}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <Users size={12} /> {website.total_visitors || 0} visitors
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageSquare size={12} /> {website.total_chats || 0} chats
                      </span>
                      {website.last_ping_at && (
                        <span className="flex items-center gap-1">
                          <Clock size={12} /> Last ping: {new Date(website.last_ping_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => openScriptDialog(website)}
                    className="gap-1.5"
                    data-testid={`get-script-${website.id}`}
                  >
                    <Code size={14} /> Get Script
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => navigate(`/live-chat/websites/${website.id}/flow-config`)}
                    className="gap-1.5"
                    data-testid={`chat-flow-config-${website.id}`}
                  >
                    <Settings size={14} /> Chat Flow
                  </Button>
                  {isSuperAdmin && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => openTransferDialog(website)}
                      title="Transfer to another organization"
                    >
                      <ArrowRightLeft size={16} />
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => openEditDialog(website)}
                  >
                    <Edit2 size={16} />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleDelete(website.id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
              
              {/* Features */}
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-100">
                <Badge variant="outline" className={website.enable_visitor_tracking ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : ''}>
                  {website.enable_visitor_tracking ? <Check size={12} className="mr-1" /> : <X size={12} className="mr-1" />}
                  Visitor Tracking
                </Badge>
                <Badge variant="outline" className={website.enable_prechat_form ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : ''}>
                  {website.enable_prechat_form ? <Check size={12} className="mr-1" /> : <X size={12} className="mr-1" />}
                  Pre-chat Form
                </Badge>
                <Badge variant="outline" className={website.enable_lead_auto_creation ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : ''}>
                  {website.enable_lead_auto_creation ? <Check size={12} className="mr-1" /> : <X size={12} className="mr-1" />}
                  Auto Lead Creation
                </Badge>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Website Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingWebsite ? 'Edit Website' : 'Add New Website'}</DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="basic" className="mt-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="widget">Widget Settings</TabsTrigger>
              <TabsTrigger value="features">Features</TabsTrigger>
            </TabsList>
            
            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Website Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="My Website"
                    data-testid="website-name-input"
                  />
                </div>
                <div>
                  <Label>Domain *</Label>
                  <Input
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    placeholder="example.com"
                    data-testid="website-domain-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Brand Name</Label>
                  <Input
                    value={formData.brand_name}
                    onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                    placeholder="Company Name"
                  />
                </div>
                <div>
                  <Label>Default Lead Source</Label>
                  <Input
                    value={formData.default_lead_source}
                    onChange={(e) => setFormData({ ...formData, default_lead_source: e.target.value })}
                    placeholder="Live Chat"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Default Team</Label>
                  <Select 
                    value={formData.default_team_id || 'none'} 
                    onValueChange={(v) => setFormData({ ...formData, default_team_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select team" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No default team</SelectItem>
                      {teams.map(team => (
                        <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="widget" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Theme Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={formData.widget_theme_color}
                      onChange={(e) => setFormData({ ...formData, widget_theme_color: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={formData.widget_theme_color}
                      onChange={(e) => setFormData({ ...formData, widget_theme_color: e.target.value })}
                      placeholder="#10b981"
                    />
                  </div>
                </div>
                <div>
                  <Label>Widget Position</Label>
                  <Select 
                    value={formData.widget_position} 
                    onValueChange={(v) => setFormData({ ...formData, widget_position: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bottom-right">Bottom Right</SelectItem>
                      <SelectItem value="bottom-left">Bottom Left</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Welcome Message</Label>
                <Textarea
                  value={formData.welcome_message}
                  onChange={(e) => setFormData({ ...formData, welcome_message: e.target.value })}
                  placeholder="Hi! How can we help you today?"
                  rows={2}
                />
              </div>
              
              <div>
                <Label>Offline Message</Label>
                <Textarea
                  value={formData.offline_message}
                  onChange={(e) => setFormData({ ...formData, offline_message: e.target.value })}
                  placeholder="We're currently offline..."
                  rows={2}
                />
              </div>
              
              {/* Widget Preview */}
              <div className="p-4 bg-slate-100 rounded-lg">
                <Label className="text-xs text-slate-500 mb-2 block">Preview</Label>
                <div className="flex justify-end">
                  <div 
                    className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center"
                    style={{ backgroundColor: formData.widget_theme_color }}
                  >
                    <MessageSquare size={24} className="text-white" />
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="features" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">Visitor Tracking</p>
                    <p className="text-sm text-slate-500">Track visitor behavior on your website</p>
                  </div>
                  <Switch
                    checked={formData.enable_visitor_tracking}
                    onCheckedChange={(v) => setFormData({ ...formData, enable_visitor_tracking: v })}
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">Pre-chat Form</p>
                    <p className="text-sm text-slate-500">Collect visitor info before starting chat</p>
                  </div>
                  <Switch
                    checked={formData.enable_prechat_form}
                    onCheckedChange={(v) => setFormData({ ...formData, enable_prechat_form: v })}
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">Auto Lead Creation</p>
                    <p className="text-sm text-slate-500">Automatically create leads from qualified chats</p>
                  </div>
                  <Switch
                    checked={formData.enable_lead_auto_creation}
                    onCheckedChange={(v) => setFormData({ ...formData, enable_lead_auto_creation: v })}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-website-btn">
              {editingWebsite ? 'Update Website' : 'Add Website'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tracking Script Dialog */}
      <Dialog open={showScriptDialog} onOpenChange={setShowScriptDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Tracking Script for {selectedWebsite?.name}</DialogTitle>
          </DialogHeader>
          
          {scriptData && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-sm text-slate-500">Website Token</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 p-2 bg-slate-100 rounded text-sm font-mono overflow-x-auto">
                    {scriptData.token}
                  </code>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => copyToClipboard(scriptData.token)}
                  >
                    <Copy size={14} />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => regenerateToken(selectedWebsite.id)}
                  >
                    <RefreshCw size={14} />
                  </Button>
                </div>
              </div>
              
              <div>
                <Label className="text-sm text-slate-500">Embed Script</Label>
                <div className="relative mt-1">
                  <pre className="p-4 bg-slate-900 text-slate-100 rounded-lg text-sm overflow-x-auto whitespace-pre-wrap">
                    {scriptData.script}
                  </pre>
                  <Button 
                    variant="secondary"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(scriptData.script)}
                  >
                    <Copy size={14} className="mr-1" /> Copy
                  </Button>
                </div>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Installation Instructions</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                  {scriptData.instructions?.map((instruction, i) => (
                    <li key={i}>{instruction}</li>
                  ))}
                </ol>
              </div>
              
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500">Verification Status:</span>
                {selectedWebsite?.verification_status === 'verified' ? (
                  <Badge className="bg-emerald-50 text-emerald-700">
                    <CheckCircle size={12} className="mr-1" /> Verified
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-amber-600">
                    <AlertCircle size={12} className="mr-1" /> Pending Verification
                  </Badge>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScriptDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Organization Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft size={20} />
              Transfer Website
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                Transfer <strong>{selectedWebsite?.name}</strong> ({selectedWebsite?.domain}) to another organization.
              </p>
            </div>
            
            <div>
              <Label>Target Organization *</Label>
              <Select 
                value={transferData.target_organization_id} 
                onValueChange={(value) => setTransferData({ ...transferData, target_organization_id: value })}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>
                      <div className="flex items-center gap-2">
                        <Building2 size={14} />
                        {org.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Transfer Chat History</Label>
                <p className="text-xs text-slate-500">Move all existing chats to the new organization</p>
              </div>
              <Switch 
                checked={transferData.transfer_chats}
                onCheckedChange={(checked) => setTransferData({ ...transferData, transfer_chats: checked })}
              />
            </div>
            
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                <AlertCircle size={14} className="inline mr-1" />
                This action will move the website and its settings to the selected organization. 
                Users in the current organization will no longer have access.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferDialog(false)}>Cancel</Button>
            <Button onClick={handleTransfer} className="gap-2">
              <ArrowRightLeft size={14} /> Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiveChatWebsites;
